import styled, { css } from 'styled-components';
import { DEVICES } from '_constants';

export const TableList = styled.ul`
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;

  /* New design: cards are spaced by the list's own gap rather than a margin on each item —
     mirrors ui-kit/DataTable/MobileRows.styles.js. See BaseTable/styles.js for the flag rationale. */
  html[data-new-ui='true'] & {
    gap: 12px;
    padding: 12px;
  }
`;

const CellWrapper = styled.button`
  border: none;
  display: flex;
  align-items: center;
  padding: 7px 17px;
  cursor: pointer;
  height: 100%;
  width: 100%;
  background: transparent;

  ${({ disabled }) =>
    disabled &&
    css`
      pointer-events: none;
      cursor: default;
      color: var(--text-strong);
    `}
`;

export const DropdownsBlock = styled.div`
  display: flex;
  align-items: center;

  > div {
    margin: 0 0 0 24px;
  }
`;

export const PaginationBlock = styled.div`
  display: flex;
  align-items: center;

  > div {
    margin: 0 0 0 24px;
  }
`;

const WrapperDropdown = styled.div.attrs(() => ({
  ariaRequired: true,
}))`
  margin-bottom: 27px;
  ${({ isRow }) =>
    isRow &&
    css`
      display: flex;
      align-items: center;
    `};

  @media ${DEVICES.tablet} {
    width: 100%;
    margin-bottom: 15px;
    ${({ isMerger }) =>
      isMerger &&
      css`
        margin-bottom: 10px;
      `}
  }

  @media ${DEVICES.mobileL} {
    width: 100%;
    margin-bottom: 5px;
  }
`;

export const Wrapper = styled.div`
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  border-right: 1px solid var(--rgb-grey);
  border-bottom: 1px solid var(--rgb-grey);
  border-left: 1px solid var(--rgb-grey);
  box-sizing: border-box;
  padding: 10px 15px;

  ${({ isStoreMerger }) =>
    isStoreMerger &&
    css`
      padding: 0;
      border: none;
    `}

  ${WrapperDropdown} {
    margin-bottom: 0;
  }

  @media ${DEVICES.laptop} {
    flex-direction: column;
    height: auto;

    ${PaginationBlock} {
      order: -1;
    }

    ${DropdownsBlock} {
      margin: 10px 0 0;
    }

    ${PaginationBlock},
    ${DropdownsBlock} {
      justify-content: space-between;
      width: 100%;

      > div {
        margin: 0;
      }
    }
  }

  @media ${DEVICES.tablet} {
    padding: 10px 5px;
  }
`;

export const TableItem = styled.li`
  display: flex;
  flex-direction: column;
  background: ${({ revisitNeeded }) => (revisitNeeded ? '#ffc5c5' : 'var(--white)')};

  &:not(:first-child) {
    margin: 15px 0 0;
  }

  /* New design: each row becomes a bordered card. The revisit highlight switches to the softer
     fill + left accent the redesigned mobile rows use. */
  html[data-new-ui='true'] & {
    border: 1px solid var(--grey);
    border-radius: 10px;
    overflow: hidden;
    background: ${({ revisitNeeded }) => (revisitNeeded ? '#fdecec' : 'var(--white)')};
    ${({ revisitNeeded }) =>
      revisitNeeded &&
      css`
        box-shadow: inset 3px 0 0 var(--danger);
      `}
  }

  /* Spacing now comes from TableList's gap. */
  html[data-new-ui='true'] &:not(:first-child) {
    margin: 0;
  }
`;

export const TableRow = styled.div`
  display: flex;
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;
  border: 1px solid var(--rgb-grey);

  &:not(:first-of-type) {
    border-top: none;
  }

  /* New design: fields are divided by a single hairline, and the last one sits flush on the
     card's rounded edge. */
  html[data-new-ui='true'] & {
    border: none;
  }

  html[data-new-ui='true'] &:not(:last-child) {
    border-bottom: 1px solid var(--grey);
  }
`;

export const TableHeader = styled.div`
  display: flex;
  align-items: center;
  background: var(--rgb-soft-grey);
  padding: 20px 10px;
  max-width: 200px;
  min-height: 100%;
  flex: 0 0 200px;
  box-sizing: border-box;

  ${CellWrapper} {
    max-width: 100%;
    padding: 0;
    width: auto;
  }

  @media ${DEVICES.mobileL} {
    max-width: 110px;
  }

  /* New design: the grey label column becomes small-caps label text on the card's own white,
     matching the redesigned mobile rows. */
  html[data-new-ui='true'] & {
    background: transparent;
    padding: 10px 12px;
    flex: 0 0 38%;
    max-width: 38%;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--dim-grey);
  }
`;

export const TableBody = styled.div`
  display: flex;
  align-items: center;
  padding: 20px 10px;
  flex: 1 0 100px;

  /* New design: tighter rhythm to match the label. Left alignment is kept deliberately — the
     redesigned rows right-align their values, but these cells hold arbitrary content (links,
     pills, action buttons) that reads worse pushed to the edge. */
  html[data-new-ui='true'] & {
    padding: 10px 12px;
  }
`;

export const TableEditCell = styled.div`
  border-top: 1px solid var(--rgb-grey);
  border-left: 1px solid var(--rgb-grey);
  border-right: 1px solid var(--rgb-grey);
  height: 42px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

export const TableEditButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 10px;
  box-sizing: border-box;
`;

export const TableEditText = styled.div`
  margin: 0 0 0 8px;
`;

export const TableColumnControl = styled.div`
  position: relative;
  height: 100%;
`;

export const TableWrapper = styled.div`
  ${TableColumnControl} {
    margin: 0 0 18px;
  }
`;
